import apiClient, { getBasePath } from './apiClient'
import { errorService } from '@/services/errorService'
import i18n from '@/i18n'
import type {
  DateAvailability,
  TablesAvailabilityResponse,
  TableBookingRequest,
  TableBookingResponse,
} from '@/types/booking.types'

const { t } = i18n.global

export const bookingService = {
  /**
   * Get tables availability from the server
   */
  async getTablesAvailability(placeId: string = 'default-restaurant', countryCode: string): Promise<Record<number, DateAvailability[]> & { _rawResponse?: TablesAvailabilityResponse }> {
    try {
      // Only add cache busting parameter to prevent caching
      const cacheBuster = new Date().getTime()

      // Get the base path from window.__BASE_PATH__
      const basePath = getBasePath()

      // Make the API call without any party size parameters
      // Use the correct URL format for the API endpoint
      // Explicitly include the base path in the URL
      const url = `${basePath}/api/tables/availability?placeId=${placeId}&countryCode=${countryCode}&_cb=${cacheBuster}`
      const response = await apiClient.get(url, {
        baseURL: '' // Override the base URL to use the full URL
      })
      const data: TablesAvailabilityResponse = response.data

      // API response received and processed successfully
      // Transform raw availability data into party-size-specific format

      // Initialize result object to store availability data by party size
      const result: Record<number, DateAvailability[]> = {}

      // Helper function to check if a time slot is in the past
      const isPastTimeSlot = (date: string, time: string): boolean => {
        if (!date || !time) {
          console.warn('Empty date or time provided to isPastTimeSlot')
          return false
        }

        try {
          // Create a date object for the slot's date and time
          const [hours, minutes] = time.split(':').map(Number)
          const slotDate = new Date(date)
          slotDate.setHours(hours, minutes, 0, 0)

          // Get current date and time
          const now = new Date()

          // Check if the slot is in the past
          const isPast = slotDate.getTime() < now.getTime()

          return isPast
        } catch (error) {
          console.error('Error checking if time slot is in the past:', error instanceof Error ? error.message : 'Unknown error')
          return false
        }
      }

      // Get party size ranges from response or use defaults
      const ranges = data.config?.partySizeRanges || [
        { min: 1, max: 2, label: '1-2' },
        { min: 3, max: 4, label: '3-4' },
        { min: 5, max: 8, label: '5-8' },
        { min: 9, max: 20, label: '9+' }
      ]

      // Create a deep copy of the availability data to avoid modifying the original
      const processedAvailability = JSON.parse(JSON.stringify(data.availability)) as typeof data.availability

      // Filter out past time slots from each day's time slots to prevent booking expired slots
      processedAvailability.forEach((day: any) => {
        if (day.isToday) {
          // For today's date, remove time slots that have already passed
          day.timeSlots = day.timeSlots.filter((slot: any) => !isPastTimeSlot(day.date, slot.time))
        }
      })

      // For each party size range
      ranges.forEach(range => {
        // For each concrete party size in the range
        for (let size = range.min; size <= range.max; size++) {
          // Transform data for this party size
          result[size] = processedAvailability.map((day: any) => {
            // Get available times for this party size range
            const availableTimes = day.timeSlots
              .filter((slot: any) => {
                // Check if the slot has tables available for this party size
                const hasAvailability = slot.exactAvailability[range.label] > 0

                // Check if the slot is in the past
                const isNotPast = !isPastTimeSlot(day.date, slot.time)

                // A time slot is available if it has any tables available AND is not in the past
                return hasAvailability && isNotPast
              })
              .map((slot: any) => slot.time)
              .sort() // Sort times chronologically

            return {
              date: day.date,
              hasAvailability: availableTimes.length > 0,
              availableTimes
            }
          })
        }
      })

      // Add the raw response to the result for access to config values
      const resultWithRaw = result as Record<number, DateAvailability[]> & { _rawResponse?: TablesAvailabilityResponse }
      resultWithRaw._rawResponse = data

      // If we're using mock data, ensure we have a maxPartySize in the response
      if (data.config?.maxPartySize === undefined && process.env.DEV_USE_MOCK_DATA === 'true') {
        // Set default maxPartySize for mock data to enable proper party size validation
        if (!resultWithRaw._rawResponse) resultWithRaw._rawResponse = {} as TablesAvailabilityResponse
        if (!resultWithRaw._rawResponse.config) resultWithRaw._rawResponse.config = {} as any
        resultWithRaw._rawResponse.config.maxPartySize = 10
      }

      return resultWithRaw
    } catch (error) {
      console.error('Error fetching table availability:', error instanceof Error ? error.message : 'Unknown error')

      // Handle the error through our error service
      const appError = errorService.handleError(error, {
        context: 'getTablesAvailability',
        placeId
      })

      // Rethrow the error for the component to handle
      throw appError
    }
  },

  async createBooking(request: TableBookingRequest): Promise<TableBookingResponse> {
    const { placeId, countryCode, date, time, partySize, guestInfo } = request
    const { name, mobile, specialRequests: note } = guestInfo

    // Create the request payload exactly as the server expects
    const requestPayload = {
      placeId,
      countryCode,
      date: date.toISOString().split('T')[0],
      time,
      partySize,
      guestInfo: {
        name,
        mobile,
        note
      }
    }

    try {
      // Get the base path from window.__BASE_PATH__
      const basePath = getBasePath()

      // Ensure we're sending with the correct content type
      // Use the correct URL format for the API endpoint
      // Explicitly include the base path in the URL
      const url = `${basePath}/api/bookings`
      const response = await apiClient.post(url, requestPayload, {
        baseURL: '', // Override the base URL to use the full URL
        headers: {
          'Content-Type': 'application/json'
        }
      })

      // Booking response received and processed successfully
      // Response contains booking confirmation, deposit info, and member status

      return response.data
    } catch (error) {
      // Booking API call failed - error details handled by error service for user-friendly messaging

      // Check if we're offline
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        // Handle the error through our error service with a specific booking error message
        const appError = errorService.handleError(error, {
          context: 'createBooking',
          request: requestPayload
        })

        // Override the message for a better user experience
        appError.message = t('errors.bookingError')

        // Rethrow the error for the component to handle
        throw appError
      }

      // Handle the error through our error service
      const appError = errorService.handleError(error, {
        context: 'createBooking',
        request: requestPayload
      })

      // Rethrow the error for the component to handle
      throw appError
    }
  }
}
